import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useWebsiteStats from "../../data/useWebsiteStats";
import { useWebsite } from "../../data/useWebsites";
import useHiddenPages from "../../data/useHiddenPages";
import LoadingWheel from "../../components/shared/LoadingWheel";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Keyed by the quote's API FurthestStep, which is not the widget's page index. The API numbering
// predates the Collection Details page: picking a quote leaves FurthestStep at 5, so from there on
// each value is one behind the page the seller is on, and 8 means the quote was completed.
// WidgetPage uses the same pre-Collection Details numbering, so a hidden page's value is its step here.
const STEP_LABELS: Record<number, string> = {
  0: "Enter VRM",
  1: "Confirm Vehicle",
  2: "Contact Details",
  3: "Engine Condition",
  4: "Body Condition",
  5: "Quote Select",
  6: "Customer Details",
  7: "Payment Details",
  8: "Completed",
};

const HIDDEN_STEP_COLOR = "rgba(156, 163, 175, 0.6)";

type Preset = "today" | "thisWeek" | "last7" | "thisMonth" | "thisYear";

function getRange(preset: Preset): { from: Date; to: Date } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "thisWeek": {
      const day = today.getDay();
      const diff = day === 0 ? 6 : day - 1;
      const monday = new Date(today);
      monday.setDate(today.getDate() - diff);
      return { from: monday, to: today };
    }
    case "last7": {
      const from = new Date(today);
      from.setDate(today.getDate() - 6);
      return { from, to: today };
    }
    case "thisMonth":
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: today };
    case "thisYear":
      return { from: new Date(now.getFullYear(), 0, 1), to: today };
  }
}

const PRESETS: { label: string; value: Preset }[] = [
  { label: "Today",       value: "today"     },
  { label: "This Week",   value: "thisWeek"  },
  { label: "Last 7 Days", value: "last7"     },
  { label: "This Month",  value: "thisMonth" },
  { label: "This Year",   value: "thisYear"  },
];

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 flex flex-col gap-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-3xl font-bold text-gray-800">{value}</p>
    </div>
  );
}

export default function WebsiteStats() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const { setInfo } = usePageNameContext();
  const [activePreset, setActivePreset] = useState<Preset>("thisMonth");
  const { from, to } = getRange(activePreset);

  const websiteQuery = useWebsite(websiteId ?? "");
  const website = websiteQuery.data?.data;
  const primaryColor = website?.primaryColor ?? "#6366f1";

  const { statsQuery, stats } = useWebsiteStats(websiteId ?? "", from, to);
  const { hiddenPages } = useHiddenPages();
  const hiddenSteps = new Set(
    (hiddenPages.data?.data ?? []).filter((p) => p.isHidden).map((p) => p.page as number)
  );

  useEffect(() => {
    setInfo({ name: "Website Stats", desc: "Quote metrics for this website" });
  }, []);

  const stepCounts = Array.from({ length: 9 }, (_, i) => {
    const entry = stats?.stepBreakdown.find((s) => s.step === i);
    return entry?.count ?? 0;
  });

  const stepIndexes = Object.keys(STEP_LABELS).map(Number);

  const chartData = {
    labels: stepIndexes.map((i) =>
      hiddenSteps.has(i) ? [STEP_LABELS[i], "(hidden)"] : STEP_LABELS[i]
    ),
    datasets: [
      {
        label: "Quotes",
        data: stepCounts,
        backgroundColor: stepIndexes.map((i) =>
          hiddenSteps.has(i) ? HIDDEN_STEP_COLOR : hexToRgba(primaryColor, 0.75)
        ),
        borderColor: stepIndexes.map((i) =>
          hiddenSteps.has(i) ? HIDDEN_STEP_COLOR : hexToRgba(primaryColor, 1)
        ),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
      },
    },
  };

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {website?.domain ?? "Website Stats"}
        </h1>
        {website?.domain && (
          <p className="text-sm text-gray-500 mt-0.5">Quote metrics</p>
        )}
      </div>

      {/* Date range presets */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            onClick={() => setActivePreset(p.value)}
            style={
              activePreset === p.value
                ? { backgroundColor: primaryColor, borderColor: primaryColor, color: "#fff" }
                : {}
            }
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              activePreset === p.value
                ? ""
                : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {statsQuery.isLoading ? (
        <LoadingWheel />
      ) : statsQuery.isError ? (
        <p className="text-red-500">Failed to load stats.</p>
      ) : stats ? (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard label="Total Quotes" value={stats.totalQuotes} />
            <StatCard label="Daily Average" value={stats.dailyAverage} />
            <StatCard label="Conversions" value={stats.convertedCount} />
            <StatCard label="Conversion Rate" value={`${stats.conversionRate}%`} />
            <StatCard label="No Match (Out of Area)" value={stats.noMatchCount} />
          </div>

          {/* Bar chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-4">
              Quotes by Furthest Step Reached
            </h2>
            <div className="flex gap-2 text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-md px-3 py-2 mb-4">
              <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-500" aria-hidden="true" />
              <div className="space-y-1">
                <p>
                  Each quote is counted once, against the furthest page the seller reached
                  before they stopped. <span className="font-semibold">Completed</span> is
                  quotes that went all the way through the widget.
                </p>
                <p>
                  <span className="font-semibold">Quote Select</span> also includes sellers who
                  picked a price but left on the Collection Details page.
                </p>
                <p>
                  Pages marked <span className="font-semibold">(hidden)</span> and greyed out are
                  hidden on your widget. They are filled in automatically with their default
                  values, so sellers pass straight through them and they will usually show zero.
                </p>
              </div>
            </div>
            <div className="relative h-64 sm:h-80 lg:h-96">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
